// Prospect-local time intelligence (Workspace only).
// All calculations use the browser's Intl APIs so daylight-saving is handled
// automatically for BOTH the prospect and the logged-in user. We never infer a
// time zone from phone/area code — only from the prospect's structured
// City/State. If a location cannot be confidently mapped, callers get null.

// Predominant IANA time zone per US state (+DC). Split-zone states map to the
// zone covering the majority of their population; notable exceptions are
// refined by CITY_TZ_OVERRIDES below.
const STATE_TZ = {
  AL: "America/Chicago", AK: "America/Anchorage", AZ: "America/Phoenix",
  AR: "America/Chicago", CA: "America/Los_Angeles", CO: "America/Denver",
  CT: "America/New_York", DE: "America/New_York", DC: "America/New_York",
  FL: "America/New_York", GA: "America/New_York", HI: "Pacific/Honolulu",
  ID: "America/Denver", IL: "America/Chicago", IN: "America/New_York",
  IA: "America/Chicago", KS: "America/Chicago", KY: "America/New_York",
  LA: "America/Chicago", ME: "America/New_York", MD: "America/New_York",
  MA: "America/New_York", MI: "America/New_York", MN: "America/Chicago",
  MS: "America/Chicago", MO: "America/Chicago", MT: "America/Denver",
  NE: "America/Chicago", NV: "America/Los_Angeles", NH: "America/New_York",
  NJ: "America/New_York", NM: "America/Denver", NY: "America/New_York",
  NC: "America/New_York", ND: "America/Chicago", OH: "America/New_York",
  OK: "America/Chicago", OR: "America/Los_Angeles", PA: "America/New_York",
  RI: "America/New_York", SC: "America/New_York", SD: "America/Chicago",
  TN: "America/Chicago", TX: "America/Chicago", UT: "America/Denver",
  VT: "America/New_York", VA: "America/New_York", WA: "America/Los_Angeles",
  WV: "America/New_York", WI: "America/Chicago", WY: "America/Denver",
};

const STATE_NAME_TO_ABBR = {
  alabama: "AL", alaska: "AK", arizona: "AZ", arkansas: "AR", california: "CA",
  colorado: "CO", connecticut: "CT", delaware: "DE", "district of columbia": "DC",
  florida: "FL", georgia: "GA", hawaii: "HI", idaho: "ID", illinois: "IL",
  indiana: "IN", iowa: "IA", kansas: "KS", kentucky: "KY", louisiana: "LA",
  maine: "ME", maryland: "MD", massachusetts: "MA", michigan: "MI",
  minnesota: "MN", mississippi: "MS", missouri: "MO", montana: "MT",
  nebraska: "NE", nevada: "NV", "new hampshire": "NH", "new jersey": "NJ",
  "new mexico": "NM", "new york": "NY", "north carolina": "NC",
  "north dakota": "ND", ohio: "OH", oklahoma: "OK", oregon: "OR",
  pennsylvania: "PA", "rhode island": "RI", "south carolina": "SC",
  "south dakota": "SD", tennessee: "TN", texas: "TX", utah: "UT",
  vermont: "VT", virginia: "VA", washington: "WA", "west virginia": "WV",
  wisconsin: "WI", wyoming: "WY",
};

// City-level overrides for well-known cities that sit in a different zone than
// their state's predominant zone (keyed by "city, ST").
const CITY_TZ_OVERRIDES = {
  "el paso, tx": "America/Denver",
  "hudspeth, tx": "America/Denver",
  "pensacola, fl": "America/Chicago",
  "gary, in": "America/Chicago",
  "hammond, in": "America/Chicago",
};

const normAbbr = (state) => {
  if (!state) return null;
  const s = String(state).trim();
  if (!s) return null;
  if (s.length === 2 && STATE_TZ[s.toUpperCase()]) return s.toUpperCase();
  const byName = STATE_NAME_TO_ABBR[s.toLowerCase()];
  return byName || null;
};

// Resolve an IANA time zone from structured City/State, or null when it cannot
// be confidently determined.
export const resolveTimeZone = (city, state) => {
  const abbr = normAbbr(state);
  if (!abbr) return null;
  if (city) {
    const key = `${String(city).trim().toLowerCase()}, ${abbr}`;
    if (CITY_TZ_OVERRIDES[key]) return CITY_TZ_OVERRIDES[key];
  }
  return STATE_TZ[abbr] || null;
};

export const getUserTimeZone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  } catch {
    return "UTC";
  }
};

// Offset (in minutes) from UTC for a given IANA zone at a given instant.
const offsetMinutes = (timeZone, date) => {
  try {
    const dtf = new Intl.DateTimeFormat("en-US", {
      timeZone, hour12: false,
      year: "numeric", month: "2-digit", day: "2-digit",
      hour: "2-digit", minute: "2-digit", second: "2-digit",
    });
    const p = dtf.formatToParts(date).reduce((a, x) => { a[x.type] = x.value; return a; }, {});
    let hour = parseInt(p.hour, 10);
    if (hour === 24) hour = 0; // some engines emit 24 for midnight
    const asUTC = Date.UTC(+p.year, +p.month - 1, +p.day, hour, +p.minute, +p.second);
    return Math.round((asUTC - date.getTime()) / 60000);
  } catch {
    return null;
  }
};

// e.g. "7:32 PM EDT"
export const formatZoneTime = (timeZone, date = new Date()) => {
  try {
    return new Intl.DateTimeFormat("en-US", {
      timeZone, hour: "numeric", minute: "2-digit", hour12: true,
      timeZoneName: "short",
    }).format(date);
  } catch {
    return null;
  }
};

// Local hour (0-23) in a zone, for business-hours checks.
export const zoneHour = (timeZone, date = new Date()) => {
  try {
    const h = new Intl.DateTimeFormat("en-US", {
      timeZone, hour: "2-digit", hour12: false,
    }).format(date);
    let n = parseInt(h, 10);
    if (n === 24) n = 0;
    return n;
  } catch {
    return null;
  }
};

// Signed hour difference between prospect zone and user zone (prospect ahead => +).
export const zoneHourDiff = (prospectTz, userTz, date = new Date()) => {
  const a = offsetMinutes(prospectTz, date);
  const b = offsetMinutes(userTz, date);
  if (a == null || b == null) return null;
  return Math.round((a - b) / 60);
};

// "Same time" | "+3 hrs" | "-2 hrs"
export const diffLabel = (n) => {
  if (n == null) return "";
  if (n === 0) return "Same time";
  const unit = Math.abs(n) === 1 ? "hr" : "hrs";
  return `${n > 0 ? "+" : "\u2212"}${Math.abs(n)} ${unit}`;
};

// Convenience bundle for the Workspace location line + email preview.
export const prospectTimeInfo = (city, state, date = new Date()) => {
  const tz = resolveTimeZone(city, state);
  if (!tz) return { known: false };
  const userTz = getUserTimeZone();
  const diff = zoneHourDiff(tz, userTz, date);
  return {
    known: true,
    tz,
    userTz,
    timeStr: formatZoneTime(tz, date),
    diff,
    diffText: diffLabel(diff),
    hour: zoneHour(tz, date),
  };
};

// True when the recipient-local hour is outside 8:00 AM – 6:00 PM.
export const isOutsideBusinessHours = (hour) =>
  hour != null && (hour < 8 || hour >= 18);
